import type { BlogPost } from '../types';

const post: BlogPost = {
    slug: 'baubit-modular-ecosystem',
    title: 'Why I Broke Baubit Apart: From Framework to Modular .NET Ecosystem',
    date: '2025-12-07',
    description:
        'How splitting Baubit from a monolithic framework into focused, composable libraries solved adoption and evolution problems.',
    tags: ['.NET', 'Architecture', 'Baubit'],
    content: `# Why I Broke Baubit Apart: From Framework to Modular .NET Ecosystem

## Background

Baubit started as a single framework attempting to solve multiple problems at once. It bundled dependency injection, mediation, caching, configuration, and identity generation into one repository with tight interdependencies.

This approach seemed efficient at first. Fewer packages to manage. Shared utilities across modules. A unified release cycle.

But it was the wrong shape. The framework became harder to evolve. Breaking changes in one component forced upgrades across the entire stack. Teams couldn't adopt individual pieces without pulling in the whole framework.

## Problems with Monolithic Frameworks

**Bundle bloat.** Applications pay for features they never use. A project needing only caching also gets mediation, configuration, and identity generation whether needed or not.

**Tight coupling.** One breaking change forces a coordinated upgrade. A fix to the caching layer requires updating every consuming service, even those using only DI or mediation.

**Adoption friction.** Existing codebases can't integrate incrementally. Teams face an all-or-nothing decision: adopt the entire framework or use none of it.

**Evolution constraints.** Individual components can't evolve at different paces. Fast-moving pieces like caching get held back by stable, mature components.

## Principles Behind the Baubit.* Ecosystem

**Small, focused packages.** Each library solves one problem with clear boundaries. No hidden dependencies between unrelated concerns.

**Configuration-first modularity.** Modules are first-class concepts with explicit configuration. This replaces scattered \`AddSingleton\` calls with structured, discoverable composition.

**Composable, not prescriptive.** Baubit libraries integrate with existing DI containers and architectures. They don't replace your stack - they augment it.

**Performance and correctness matter.** These libraries often sit in hot paths: request handling, caching, message routing. Correctness isn't negotiable when dealing with distributed IDs or multi-tier cache invalidation.

**Compatibility.** Target the lowest possible framework for broadest compatibility. Most projects target netstandard2.0.

## Overview of Key Packages

**Baubit.DI** provides modularity and configuration-driven composition. It treats modules as the primary building block, with explicit dependencies and lifecycle management.

**Baubit.DI.Autofac** and **Baubit.DI.Extensions** integrate Baubit modules with Autofac and Microsoft's \`IServiceCollection\`. This allows teams to keep their existing DI container while adopting Baubit's module pattern.

**Baubit.Configuration** offers type-safe configuration with the result pattern and environment variable expansion. Configuration errors surface early with detailed diagnostics.

**Baubit.Caching** implements a two-tier cache architecture: bounded L1 for hot data, unbounded L2 for durability. O(1) lookups with ordered async enumeration for bulk operations.

**Baubit.Caching.LiteDB** backs the L2 cache with LiteDB for persistence. This enables durable caching and rewind/replay scenarios where cache state must survive restarts.

**Baubit.Mediation** provides message routing with a subscription model. It enforces exactly one handler per message type, eliminating ambiguity in request handling.

**Baubit.Identity** generates monotonic GuidV7 identifiers for distributed systems. Monotonic ordering improves database index performance and simplifies time-based queries.

**Baubit.Traceability** tracks results and traces across application layers. It provides structured context for debugging and observability.

**Baubit.Testing** supplies scenario-based test helpers for unit testing. It reduces boilerplate in integration tests that involve multiple setup steps.

## Unifying Design Language

Despite being separate packages, Baubit libraries share common design patterns.

Modules are the primary composition building block. Instead of registering services individually, you compose modules that encapsulate related services, configuration, and lifecycle concerns.

Configuration is explicit and type-safe. Each module declares its configuration requirements. Missing or invalid configuration surfaces at startup, not during runtime when a feature is first accessed.

Integration over replacement. Baubit libraries work with your existing DI container. They don't force you to replace ASP.NET Core's built-in container or adopt a new application model.

Observability and testability are built in. Traceability hooks, result patterns, and scenario-based testing make it easier to understand what's happening and verify behavior.

## Example

Here's how Baubit components compose together in an ASP.NET Core application:

\`\`\`csharp
var builder = WebApplication.CreateBuilder(args);

// Register Baubit modules via DI extensions
builder.Services.AddModule<CacheModule, CacheModuleConfiguration>(cfg =>
{
    cfg.L1MaxCap = 100;
    cfg.DatabasePath = "cache.db",
    cfg.CollectionName = "users";
});

builder.Services.AddModule<MediationModule, MediationModuleConfiguration>(cfg =>
{

});

var app = builder.Build();

// Example handler using both caching and mediation
public class GetUserQueryHandler : IRequestHandler<GetUserQuery, User>
{
    private readonly IOrderedCache<User> _userCache;
    
    public GetUserQueryHandler(IOrderedCache<User> userCache)
    {
        _userCache = userCache;
    }
    
    public async Task<User> Handle(GetUserQuery request)
    {
        var cacheKey = $"user:{request.UserId}";
        
        // Check L1 cache first, then L2, then load from database
        var user = await _userCache.GetEntryOrDefault(cacheKey,
                   out var cacheEntry) && cacheEntry != null ?
                   cacheEntry.Value :
                   await LoadUserFromDatabase(request.UserId);
        );
        
        return user;
    }
}

// Send query through mediator
app.MapGet("/users/{id}", async (Guid id, IMediator mediator) =>
{
    var query = new GetUserQuery(id);
    var user = await mediator.PublishAsync(query);
    return Results.Ok(user);
});
\`\`\`

This example shows three Baubit libraries working together. The DI module registers the cache and mediator. The mediator routes the query to the handler. The handler uses the two-tier cache to avoid redundant database calls.

## Why This Matters

Splitting Baubit into focused packages solves the adoption problem. Teams can integrate caching without adopting mediation. They can use GuidV7 generation without pulling in the entire ecosystem.

Individual libraries can evolve independently. A breaking change in the caching layer doesn't force mediator users to upgrade. Performance improvements ship faster because they don't wait on unrelated components.

This avoids framework lock-in while maintaining a coherent toolbox. Each piece follows the same design principles: modules, configuration-first, explicit dependencies, and observability. But you're free to use one, some, or all of them.

## What's Next

Future posts will explore specific components in depth:

- Deep dive into Baubit.DI module composition and lifecycle management
- Internals of the ordered L1/L2 cache: eviction policies and enumeration
- Baubit.Mediation design decisions, subscription model, and benchmarks
- Testing and traceability patterns with Baubit.Testing and Baubit.Traceability

Each post will examine real-world scenarios, design trade-offs, and performance characteristics.`,
};

export default post;
